import { useCallback, useMemo, useRef, useState } from 'react'
import type { AppendixCodeItemOption, AppendixColumnDataType, AppendixReferenceTargets } from '../types/codeIndex'
import {
  AppendixColumnDraft,
  AppendixColumnError,
  AppendixColumnRef,
  AppendixRowDraft,
  applyColumnTypeChange,
  buildAppendixCodeItemOptions,
  buildRefLabelIndex,
  columnRefFromOption,
  createColumnDraft,
  columnRefSignature,
  createRowDraft,
  describeInvalidValues,
  findCellsClearedByTypeChange,
  findCodeItemOption,
  formatColumnRefCode,
  formatCount,
  getColumnTypeNoun,
  moveItem,
  removeColumn,
  validateCell,
  validateColumns,
} from '../utils/appendixTable'

type TableDraft = {
  columns: AppendixColumnDraft[]
  rows: AppendixRowDraft[]
}

export type AppendixUndoToast = {
  id: number
  title: string
  message: string
}

export type PendingColumnTypeChange = {
  columnKey: string
  columnLabel: string
  fromType: AppendixColumnDataType
  fromCodeItemCode: string
  toType: AppendixColumnDataType
  toCodeItemCode: string
  toCodeItem: AppendixCodeItemOption | null
  clearedValues: string[]
}

type UndoState = {
  snapshot: TableDraft
  toast: AppendixUndoToast
}

const EMPTY_DRAFT: TableDraft = { columns: [], rows: [] }

export function getCellErrorKey(rowKey: string, columnKey: string): string {
  return `${rowKey}::${columnKey}`
}

function columnDisplayLabel(column: AppendixColumnDraft, columns: AppendixColumnDraft[]): string {
  return column.label.trim() || `Column ${columns.indexOf(column) + 1}`
}

function withoutColumnErrors(errors: Record<string, string>, columnKey: string): Record<string, string> {
  const suffix = `::${columnKey}`
  return Object.fromEntries(Object.entries(errors).filter(([key]) => !key.endsWith(suffix)))
}

function withoutRowErrors(errors: Record<string, string>, rowKey: string): Record<string, string> {
  const prefix = `${rowKey}::`
  return Object.fromEntries(Object.entries(errors).filter(([key]) => !key.startsWith(prefix)))
}

function withError(errors: Record<string, string>, key: string, error: string | null): Record<string, string> {
  if (error) return errors[key] === error ? errors : { ...errors, [key]: error }
  return withoutKey(errors, key)
}

function withoutKey<T>(errors: Record<string, T>, key: string): Record<string, T> {
  if (!(key in errors)) return errors
  const { [key]: _removed, ...rest } = errors
  return rest
}

function sameRef(a: AppendixColumnRef, b: AppendixColumnRef): boolean {
  return (a.codeItemKey ?? null) === (b.codeItemKey ?? null)
    && (a.codeItemGroup ?? null) === (b.codeItemGroup ?? null)
    && (a.codeItemPosition ?? null) === (b.codeItemPosition ?? null)
    && (a.codeItemPositionEnd ?? null) === (b.codeItemPositionEnd ?? null)
}

// `targets` are the saved code items and values the appendix can point at (null until loaded).
// A table that defines valid combinations allows blank cells and keeps at least one code item column.
export function useAppendixTableDraft(targets: AppendixReferenceTargets | null, definesValidCombinations = false) {
  const [draft, setDraft] = useState<TableDraft>(EMPTY_DRAFT)
  const [cellErrors, setCellErrors] = useState<Record<string, string>>({})
  const [columnErrors, setColumnErrors] = useState<Record<string, AppendixColumnError>>({})
  const [pendingTypeChange, setPendingTypeChange] = useState<PendingColumnTypeChange | null>(null)
  const [undoState, setUndoState] = useState<UndoState | null>(null)

  const { columns, rows } = draft
  const refIndex = useMemo(() => buildRefLabelIndex(targets), [targets])
  const codeItems = useMemo(() => buildAppendixCodeItemOptions(targets), [targets])
  // What each loaded column pointed at, so a reference loaded broken and left untouched still saves.
  const savedRefsRef = useRef<Map<string, string>>(new Map())

  // Replaces the whole draft, e.g. with an empty table or one loaded from the server.
  const reset = useCallback((next: TableDraft = EMPTY_DRAFT) => {
    savedRefsRef.current = new Map(next.columns.map(column => [column.key, columnRefSignature(column)]))
    setDraft(next)
    setCellErrors({})
    setColumnErrors({})
    setPendingTypeChange(null)
    setUndoState(null)
  }, [])

  // Undo only restores the draft as it was right before the action, so any later edit retires it.
  const updateDraft = (next: TableDraft) => {
    setDraft(next)
    setUndoState(null)
  }

  const showUndo = (snapshot: TableDraft, title: string, message: string) => {
    setUndoState(current => ({
      snapshot,
      toast: { id: (current?.toast.id ?? 0) + 1, title, message },
    }))
  }

  const validateCellValue = (column: AppendixColumnDraft, value: string, savedValue?: string) =>
    validateCell(column, value, refIndex, definesValidCombinations, savedValue)

  const savedValueAt = (row: AppendixRowDraft | undefined, columnKey: string) => row?.cellMeta?.[columnKey]?.savedValue

  const addColumn = (): string => {
    const column = createColumnDraft()
    updateDraft({
      columns: [...columns, column],
      rows: rows.map(row => ({ ...row, cells: { ...row.cells, [column.key]: '' } })),
    })
    return column.key
  }

  const updateColumn = (columnKey: string, patch: Partial<Pick<AppendixColumnDraft, 'label' | 'unit'>>) => {
    updateDraft({
      columns: columns.map(column => column.key === columnKey ? { ...column, ...patch } : column),
      rows,
    })
    setColumnErrors(current => withoutKey(current, columnKey))
  }

  const applyTypeChange = (columnKey: string, toType: AppendixColumnDataType, toCodeItem: AppendixCodeItemOption | null) => {
    const result = applyColumnTypeChange(columns, rows, columnKey, toType, toCodeItem, refIndex)
    setDraft({ columns: result.columns, rows: result.rows })
    setCellErrors(current => withoutColumnErrors(current, columnKey))
    setColumnErrors(current => withoutKey(current, columnKey))
    return result
  }

  // Changes that would clear cells ask first; anything else applies right away. Re-pointing a code
  // item column (another code item or position) clears the values that no longer fit it. `toChoice`
  // is the picked option (codeItemChoice) of a code item column.
  const requestColumnTypeChange = (columnKey: string, toType: AppendixColumnDataType, toChoice = '') => {
    const column = columns.find(item => item.key === columnKey)
    if (!column) return
    if (definesValidCombinations && column.dataType === 'CodeItem' && toType !== 'CodeItem'
      && columns.filter(item => item.dataType === 'CodeItem').length === 1) return

    const nextCodeItem = toType === 'CodeItem' ? findCodeItemOption(codeItems, toChoice) : null
    const nextRef = columnRefFromOption(nextCodeItem)
    if (column.dataType === toType && sameRef(column, nextRef)) return

    const clearedValues = findCellsClearedByTypeChange(columns, rows, columnKey, toType, nextCodeItem, refIndex)

    if (clearedValues.length > 0) {
      setPendingTypeChange({
        columnKey,
        columnLabel: columnDisplayLabel(column, columns),
        fromType: column.dataType,
        fromCodeItemCode: formatColumnRefCode(column, refIndex),
        toType,
        toCodeItemCode: formatColumnRefCode(nextRef, refIndex),
        toCodeItem: nextCodeItem,
        clearedValues,
      })
      return
    }

    applyTypeChange(columnKey, toType, nextCodeItem)
    setUndoState(null)
  }

  const confirmTypeChange = () => {
    if (!pendingTypeChange) return

    const snapshot = draft
    const { columnKey, columnLabel, toType, toCodeItemCode, toCodeItem } = pendingTypeChange
    const { clearedCount } = applyTypeChange(columnKey, toType, toCodeItem)
    setPendingTypeChange(null)

    const verb = clearedCount === 1 ? 'cell held a value that wasn\'t' : 'cells held values that weren\'t'
    const outcome = clearedCount === 1 ? 'It has been cleared.' : 'They have been cleared.'
    showUndo(
      snapshot,
      toType === 'CodeItem' && pendingTypeChange.fromType === 'CodeItem'
        ? `${columnLabel} now references code item ${toCodeItemCode}`
        : `${columnLabel} is now a ${getColumnTypeNoun(toType)} column`,
      `${clearedCount} ${verb} ${describeInvalidValues(toType, toCodeItemCode, clearedCount)}. ${outcome}`
    )
  }

  const cancelTypeChange = () => setPendingTypeChange(null)

  const deleteColumn = (columnKey: string) => {
    const column = columns.find(item => item.key === columnKey)
    if (!column) return

    if (definesValidCombinations && column.dataType === 'CodeItem'
      && columns.filter(item => item.dataType === 'CodeItem').length === 1) return
    const snapshot = draft
    const label = columnDisplayLabel(column, columns)
    const result = removeColumn(columns, rows, columnKey)
    setDraft({ columns: result.columns, rows: result.rows })
    setCellErrors(current => withoutColumnErrors(current, columnKey))
    setColumnErrors(current => withoutKey(current, columnKey))

    const deleted = `${formatCount(result.removedCount, 'value')} ${result.removedCount === 1 ? 'was' : 'were'} deleted`
    const removedCode = column.dataType === 'CodeItem' && column.codeItemKey ? refIndex.codeItems.get(column.codeItemKey)?.code || '' : ''
    const reference = removedCode ? `, and the reference to code item ${removedCode} is gone` : ''
    showUndo(snapshot, `${label} column removed`, `${deleted}${reference}.`)
  }

  const moveColumn = (fromIndex: number, toIndex: number) => {
    const nextColumns = moveItem(columns, fromIndex, toIndex)
    if (nextColumns !== columns) updateDraft({ columns: nextColumns, rows })
  }

  const addRow = () => {
    if (columns.length === 0) return
    updateDraft({ columns, rows: [...rows, createRowDraft(columns)] })
  }

  const deleteRow = (rowKey: string) => {
    updateDraft({ columns, rows: rows.filter(row => row.key !== rowKey) })
    setCellErrors(current => withoutRowErrors(current, rowKey))
  }

  // Adds one row per entry, each mapping a column key to its value; columns not named stay empty.
  const appendRows = (entries: Array<Record<string, string>>) => {
    if (columns.length === 0 || entries.length === 0) return
    const added = entries.map(cells => {
      const row = createRowDraft(columns)
      return { ...row, cells: { ...row.cells, ...cells } }
    })
    updateDraft({ columns, rows: [...rows, ...added] })
  }

  const setCellError = (rowKey: string, column: AppendixColumnDraft, value: string) => {
    const key = getCellErrorKey(rowKey, column.key)
    const savedValue = savedValueAt(rows.find(row => row.key === rowKey), column.key)
    setCellErrors(current => withError(current, key, validateCellValue(column, value, savedValue)))
  }

  // Cells validate on blur; a cell already flagged revalidates while typing so the error clears once fixed.
  const updateCell = (rowKey: string, columnKey: string, value: string, validateNow = false) => {
    updateDraft({
      columns,
      rows: rows.map(row => row.key === rowKey ? { ...row, cells: { ...row.cells, [columnKey]: value } } : row),
    })

    const column = columns.find(item => item.key === columnKey)
    if (column && (validateNow || cellErrors[getCellErrorKey(rowKey, columnKey)])) {
      setCellError(rowKey, column, value)
    }
  }

  // Empties a cell, e.g. one pointing at a value that no longer exists.
  const clearCell = (rowKey: string, columnKey: string) => {
    updateDraft({
      columns,
      rows: rows.map(row => row.key === rowKey ? { ...row, cells: { ...row.cells, [columnKey]: '' } } : row),
    })
    setCellErrors(current => withoutKey(current, getCellErrorKey(rowKey, columnKey)))
  }

  const validateCellAt = (rowKey: string, columnKey: string) => {
    const column = columns.find(item => item.key === columnKey)
    const row = rows.find(item => item.key === rowKey)
    if (!column || !row) return
    setCellError(rowKey, column, row.cells[columnKey] ?? '')
  }

  const validateAll = (): { columnsValid: boolean; cellsValid: boolean } => {
    const nextColumnErrors = validateColumns(columns, refIndex, savedRefsRef.current)
    const nextCellErrors: Record<string, string> = {}
    rows.forEach(row => {
      columns.forEach(column => {
        const error = validateCellValue(column, row.cells[column.key] ?? '', savedValueAt(row, column.key))
        if (error) nextCellErrors[getCellErrorKey(row.key, column.key)] = error
      })
    })

    setColumnErrors(nextColumnErrors)
    setCellErrors(nextCellErrors)
    return {
      columnsValid: Object.keys(nextColumnErrors).length === 0,
      cellsValid: Object.keys(nextCellErrors).length === 0,
    }
  }

  const undo = () => {
    if (!undoState) return
    setDraft(undoState.snapshot)
    setCellErrors({})
    setColumnErrors({})
    setUndoState(null)
  }

  const dismissUndo = useCallback(() => setUndoState(null), [])

  const firstCellError = useMemo(() => {
    for (const row of rows) {
      for (const column of columns) {
        const error = cellErrors[getCellErrorKey(row.key, column.key)]
        if (error) return error
      }
    }
    return null
  }, [cellErrors, columns, rows])

  const firstColumnError = useMemo(
    () => columns.map(column => columnErrors[column.key]?.message).find(Boolean) || null,
    [columnErrors, columns]
  )

  return {
    columns,
    rows,
    codeItems,
    refIndex,
    cellErrors,
    columnErrors,
    firstCellError,
    firstColumnError,
    pendingTypeChange,
    undoToast: undoState?.toast ?? null,
    reset,
    addColumn,
    updateColumn,
    requestColumnTypeChange,
    confirmTypeChange,
    cancelTypeChange,
    deleteColumn,
    moveColumn,
    addRow,
    deleteRow,
    appendRows,
    updateCell,
    clearCell,
    validateCellAt,
    validateAll,
    undo,
    dismissUndo,
  }
}

export default useAppendixTableDraft
